/** Teams desktop ships "Teams/<version>" and the mobile clients "TeamsMobile-<os>". */
const TEAMS_USER_AGENT_RE = /\bTeams(Mobile)?[/-]/i;

/**
 * Origins that host a Teams tab: the Teams web clients plus the Microsoft 365 /
 * Outlook shells that render Teams apps. Anchored at both ends so a look-alike
 * such as `https://teams.microsoft.com.evil.example` does not match.
 */
const TEAMS_HOST_ORIGIN_RE =
  /^https:\/\/([a-z0-9-]+\.)*(teams\.microsoft\.com|teams\.microsoft\.us|teams\.live\.com|teams\.cloud\.microsoft|m365\.cloud\.microsoft|outlook\.cloud\.microsoft|outlook\.office\.com|outlook\.office365\.com|office\.com|microsoft365\.com)$/i;

/**
 * The origins of the frames this document is embedded in, outermost included.
 * `ancestorOrigins` is the exact signal (Chromium/Safari); Firefox does not
 * implement it, so the initial referrer — the embedding page — stands in.
 */
function embedderOrigins(): string[] {
  const ancestors: DOMStringList | undefined = window.location.ancestorOrigins;
  if (ancestors !== undefined && ancestors.length > 0) {
    return Array.from(ancestors);
  }
  const referrer = document.referrer;
  if (referrer.length === 0) return [];
  try {
    return [new URL(referrer).origin];
  } catch {
    return [];
  }
}

/** Where a confirmed Teams host is remembered, per tab. */
const TEAMS_HOST_KEY = 'ct-host-teams';

/**
 * A tab cannot stop being a Teams tab, so the first positive answer stands for
 * the rest of its life. This is not an optimisation: on Firefox, which does not
 * implement `ancestorOrigins`, the standing signal is `document.referrer` — and a
 * same-origin document navigation inside the frame rewrites it to our own origin.
 * Detection would then flip to "standalone", renewal would take the Entra path
 * with no MSAL account behind it, and the tab would end up offering a sign-in
 * screen inside the Teams iframe, where a popup cannot open. sessionStorage keeps
 * the answer across such a navigation and is per-tab, so nothing leaks between
 * hosts; if it is unavailable the module-level copy still covers the page's life.
 */
let confirmedTeamsHost = false;

function rememberTeamsHost(): true {
  confirmedTeamsHost = true;
  try {
    sessionStorage.setItem(TEAMS_HOST_KEY, '1');
  } catch {
    /* storage blocked in this frame — the module-level flag stands in */
  }
  return true;
}

function recalledTeamsHost(): boolean {
  if (confirmedTeamsHost) return true;
  try {
    return sessionStorage.getItem(TEAMS_HOST_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Host detection: true only on a real Microsoft Teams signal — the desktop and
 * mobile clients identify themselves in the user agent, and the Teams web
 * client frames the tab in one of the known Teams/M365 host origins.
 *
 * Being framed is NOT a Teams signal on its own: the app is also embedded in
 * plain browser contexts, and routing those into the Teams path burns the SDK
 * timeouts on `getAuthToken()` and drops the session into the renewal cooldown.
 * An unrecognisable host is treated as a browser, which renews through Entra.
 */
export function isRunningInTeams(): boolean {
  try {
    if (TEAMS_USER_AGENT_RE.test(navigator.userAgent))
      return rememberTeamsHost();
    // Not framed is not Teams — a tab is always a frame there. Checked before the
    // remembered answer on purpose: sessionStorage is per tab, not per document,
    // so the same tab later opening this app at the top level must not inherit a
    // Teams verdict from the frame it used to hold.
    if (window.self === window.top) return false;
    if (recalledTeamsHost()) return true;
    return embedderOrigins().some((origin) => TEAMS_HOST_ORIGIN_RE.test(origin))
      ? rememberTeamsHost()
      : false;
  } catch {
    return false;
  }
}

/** Test-only: forgets a remembered Teams host. */
export function _resetTeamsHostForTests(): void {
  confirmedTeamsHost = false;
  try {
    sessionStorage.removeItem(TEAMS_HOST_KEY);
  } catch {
    /* nothing to clear */
  }
}
